import { Injectable } from '@angular/core';
import { AuthService } from 'src/app/services/auth.service';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import MapView from '@arcgis/core/views/MapView';
import Sketch from '@arcgis/core/widgets/Sketch';
import Graphic from '@arcgis/core/Graphic';
import Polygon from '@arcgis/core/geometry/Polygon';
import SimpleFillSymbol from '@arcgis/core/symbols/SimpleFillSymbol';
import SimpleLineSymbol from '@arcgis/core/symbols/SimpleLineSymbol';
import { ShapeItem } from '../constants/shape-item.interface';
import { EventEmitter } from '@angular/core';
import proj4 from 'proj4';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class SketchingService {
  private sketchLayer: GraphicsLayer | null = null;
  private sketchWidget: Sketch | null = null;
  private mapView: MapView | null = null;
  private isInitialized = false;
  private pendingShapes: ShapeItem[] = [];
  private internalUser: boolean = false;

  constructor(private authService: AuthService, private translate: TranslateService) {
    this.authService.internalUser$.subscribe((isInternalUser) => {
      this.internalUser = isInternalUser;
    });
  }

  // Event emitters for shape changes
  public onShapesChanged = new EventEmitter<ShapeItem[]>();
  public onSketchReady = new EventEmitter<void>();

  /**
   * Initialize the sketching functionality
   */
  public initializeSketching(mapView: MapView): void {
    if (this.isInitialized) {
      console.warn('Sketching already initialized');
      return;
    }

    this.mapView = mapView;
    this.createSketchLayer();
    this.createSketchWidget();
    this.setupEventListeners();
    this.isInitialized = true;
  }

  private createSketchLayer(): void {
    const existingLayer = this.mapView?.map?.layers.find(
      layer => layer.title === this.translate.instant('iaa.map.sketchLayer')
    ) as GraphicsLayer;

    if (existingLayer) {
      this.sketchLayer = existingLayer;
    } else {
      this.sketchLayer = new GraphicsLayer({
        title: this.translate.instant('iaa.map.sketchLayer'),
        visible: true,
        opacity: 1,
      });
      this.mapView?.map?.add(this.sketchLayer);
      
      if (this.mapView?.map?.layers && this.mapView.map.layers.length > 1) {
        const layers = this.mapView.map.layers;
        const lastIndex = layers.length - 1;
        layers.reorder(this.sketchLayer, lastIndex);
      }
    }
  }

  private createSketchWidget(): void {
    if (!this.sketchLayer || !this.mapView) {
      console.error('Cannot create sketch widget: missing dependencies');
      return;
    }

    this.sketchWidget = new Sketch({
      layer: this.sketchLayer,
      view: this.mapView,
      creationMode: "update",
      defaultUpdateOptions: {
        tool: "reshape"
      },
      visibleElements: {
        createTools: {
          point: false,
          polygon: true,
          polyline: false,
        },
        selectionTools: {
          "lasso-selection": true,
          "rectangle-selection": true,
        },
        undoRedoMenu: false,
        settingsMenu: false,
      }
    });

    this.mapView.ui.add(this.sketchWidget, "top-right");
  }

  private setupEventListeners(): void {
    if (!this.sketchWidget) {
      console.error('Cannot setup event listeners: sketch widget not created');
      return;
    }

    this.sketchWidget.on("update", (event: any) => {
      this.handleSketchUpdate(event);
    });

    this.sketchWidget.on("create", (event: any) => {
      this.handleSketchCreate(event);
    });

    this.sketchWidget.on("delete", (event: any) => {
      this.handleSketchDelete(event);
    });
  }

  private handleSketchUpdate(event: any): void {
    this.emitShapesChanged();
  }

  private handleSketchCreate(event: any): void {
    this.emitShapesChanged();
  }

  private handleSketchDelete(event: any): void {
    this.emitShapesChanged();
  }

  private emitShapesChanged(): void {
    const shapes = this.getCurrentShapes();
    
    this.ensureSketchLayerOnTop();
    
    this.onShapesChanged.emit(shapes);
  }

  public getCurrentShapes(): ShapeItem[] {
    if (!this.sketchLayer) {
      return [];
    }

    const shapes: ShapeItem[] = [];
    const graphics = this.sketchLayer.graphics;

    for (let i = 0; i < graphics.length; i++) {
      const graphic = graphics.getItemAt(i);
      if (graphic) {
        try {
          const shape = this.convertGraphicToShape(graphic, i.toString());
          shapes.push(shape);
        } catch (error) {
          console.error('Error converting graphic to shape:', error);
        }
      }
    }

    return shapes;
  }

  private convertGraphicToShape(graphic: Graphic, shapeId: string): ShapeItem {
    // Convert geometry to WKT format
    let wkt = this.geometryToWKT(graphic.geometry);

    // Convert WKT from EPSG:3857 to EPSG:2039 for external users (like in old code)
    if (!this.internalUser) {
      wkt = this.convertWKT3857to2039(wkt);
    }

    return {
      wkt: wkt,
      layerName: this.translate.instant('iaa.map.sketchLayer'),
      hidden: false,
      styledParams: {
        fillColor: [0, 0, 0, 0.08],
        borderColor: [0, 0, 0, 1],
        borderWidth: 2,
      }
    };
  }

  private geometryToWKT(geometry: any): string {
    if (!geometry) {
      return '';
    }

    try {
      if (geometry.type === 'polygon') {
        const rings = geometry.rings;
        if (rings && rings.length > 0) {
          const outerRing = rings[0];
          const coordinates = outerRing.map((coord: number[]) => `${coord[0]} ${coord[1]}`).join(', ');
          return `POLYGON((${coordinates}))`;
        }
      }
      return '';
    } catch (error) {
      console.error('Error converting geometry to WKT:', error);
      return '';
    }
  }

  public restoreShapes(shapes: ShapeItem[]): void {
    if (!this.sketchLayer || !this.mapView) {
      console.warn('Cannot restore shapes: sketching not initialized');
      this.pendingShapes = shapes;
      return;
    }


    this.sketchLayer.removeAll();

    shapes.forEach((shape, index) => {
      try {
        const graphic = this.convertShapeToGraphic(shape);
        if (graphic) {
          this.sketchLayer?.add(graphic);
        }
      } catch (error) {
        console.error('Error restoring shape:', error, shape);
      }
    });

    this.pendingShapes = [];

    this.ensureSketchLayerOnTop();
  }

  private convertShapeToGraphic(shape: ShapeItem): Graphic | null {
    try {
      // For external users, convert WKT from EPSG:2039 back to EPSG:3857
      let wktToUse = shape.wkt;
      if (!this.internalUser) {
        wktToUse = this.convertWKT2039to3857(shape.wkt);
      }

      // Parse WKT to get coordinates
      const coordinates = wktToUse.match(/-?\d+\.?\d*/g);
      if (!coordinates) {
        console.error('Invalid WKT format:', shape.wkt);
        return null;
      }

      // Create polygon rings
      const rings = [];
      for (let i = 0; i < coordinates.length; i += 2) {
        rings.push([parseFloat(coordinates[i]), parseFloat(coordinates[i + 1])]);
      }

      // Create polygon geometry
      const geometry = new Polygon({
        rings: [rings],
        spatialReference: { wkid: this.internalUser ? 2039 : 3857 }
      });

      // Create symbol
      const symbol = new SimpleFillSymbol({
        color: shape.styledParams.fillColor,
        outline: new SimpleLineSymbol({
          color: shape.styledParams.borderColor,
          width: shape.styledParams.borderWidth
        })
      });

      // Create graphic
      return new Graphic({
        geometry: geometry,
        symbol: symbol,
        attributes: {
          shape: shape
        }
      });
    } catch (error) {
      console.error('Error converting shape to graphic:', error);
      return null;
    }
  }

  public clearShapes(): void {
    if (this.sketchLayer) {
      this.sketchLayer.removeAll();
      this.emitShapesChanged();
    }
  }

  public isSketchingInitialized(): boolean {
    return this.isInitialized;
  }

  public getSketchLayer(): GraphicsLayer | null {
    return this.sketchLayer;
  }

  public ensureSketchLayerOnTop(): void {
    if (this.sketchLayer && this.mapView?.map?.layers && this.mapView.map.layers.length > 1) {
      const layers = this.mapView.map.layers;
      const lastIndex = layers.length - 1;
      layers.reorder(this.sketchLayer, lastIndex);
    }
  }

  public getSketchWidget(): Sketch | null {
    return this.sketchWidget;
  }

  public handleDialogReopen(): void {
    if (this.pendingShapes.length > 0 && this.isInitialized) {
      this.restoreShapes(this.pendingShapes);
    }
  }

  public destroy(): void {
    if (this.sketchWidget) {
      this.sketchWidget.destroy();
      this.sketchWidget = null;
    }

    if (this.sketchLayer) {
      this.sketchLayer.removeAll();
      this.sketchLayer = null;
    }

    this.mapView = null;
    this.isInitialized = false;
    this.pendingShapes = [];
  }

  private isValidWKT(wkt: string): boolean {
    const wktTypes = [
      'POINT',
      'LINESTRING',
      'POLYGON',
      'MULTIPOINT',
      'MULTILINESTRING',
      'MULTIPOLYGON',
    ];
    const wktStart = wkt.trim().split('(')[0].trim();
    return wktTypes.includes(wktStart);
  }

  private convertWKT3857to2039(wkt: string): string {
    if (!wkt || !this.isValidWKT(wkt)) {
      throw new Error('Invalid WKT format');
    }
    const EPSG3857 =
      '+proj=merc +a=6378137 +b=6378137 +lat_ts=0 +lon_0=0 +x_0=0 +y_0=0 +k=1 +units=m +nadgrids=@null +wktext +no_defs';
    const EPSG2039 =
      '+proj=tmerc +lat_0=31.73439361111111 +lon_0=35.20451694444445 +k=1.0000067 +x_0=219529.584 +y_0=626907.39 +ellps=GRS80 +units=m +no_defs';

    proj4.defs('EPSG:3857', EPSG3857);
    proj4.defs('EPSG:2039', EPSG2039);

    const coordsRegex = /(-?\d+\.?\d*)\s+(-?\d+\.?\d*)/g;

    const matches = [...wkt.matchAll(coordsRegex)];

    if (matches.length === 0) {
      throw new Error('No coordinates found in WKT');
    }

    const convertedWkt = wkt.replace(coordsRegex, (match, x, y) => {
      const point: any = {
        x: parseFloat(x),
        y: parseFloat(y),
      };

      const transformed = proj4('EPSG:3857', 'EPSG:2039', [point.x, point.y]);

      return `${transformed[0].toFixed(3)} ${transformed[1].toFixed(3)}`;
    });

    return convertedWkt;
  }

  private convertWKT2039to3857(wkt: string): string {
    if (!wkt || !this.isValidWKT(wkt)) {
      throw new Error('Invalid WKT format');
    }
    const EPSG3857 =
      '+proj=merc +a=6378137 +b=6378137 +lat_ts=0 +lon_0=0 +x_0=0 +y_0=0 +k=1 +units=m +nadgrids=@null +wktext +no_defs';
    const EPSG2039 =
      '+proj=tmerc +lat_0=31.73439361111111 +lon_0=35.20451694444445 +k=1.0000067 +x_0=219529.584 +y_0=626907.39 +ellps=GRS80 +units=m +no_defs';

    proj4.defs('EPSG:3857', EPSG3857);
    proj4.defs('EPSG:2039', EPSG2039);

    const coordsRegex = /(-?\d+\.?\d*)\s+(-?\d+\.?\d*)/g;

    const matches = [...wkt.matchAll(coordsRegex)];

    if (matches.length === 0) {
      throw new Error('No coordinates found in WKT');
    }

    const convertedWkt = wkt.replace(coordsRegex, (match, x, y) => {
      const point: any = {
        x: parseFloat(x),
        y: parseFloat(y),
      };

      const transformed = proj4('EPSG:2039', 'EPSG:3857', [point.x, point.y]);

      return `${transformed[0].toFixed(3)} ${transformed[1].toFixed(3)}`;
    });

    return convertedWkt;
  }
} 